import React, {useMemo} from 'react';
import {NAVIGATION_STATUSES, useReservationSettings} from 'hooks';
import {MODAL_FORMS} from './constants';
import {getAgeLimitByCountry} from 'utils/reservationSettings/utils';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useGetReservationType} from 'hooks/useGetReservationType';
import {useComputedOnboardingDetails} from 'hooks/useComputedOnboardingDetails';
import {useKioskoStore} from 'context/kiosko';
import {ReservationSettingsModal} from './ReservationSettingsModal';
import {useModalControls} from '@chekinapp/ui';
import {useIsReservationSettingsReady} from './useIsReservationSettingsReady';
import {useOpenGuestCountBlock} from './useOpenGuestCountBlock';

type UseReservationSettingsModalProps = {
  showFirstTime?: boolean;
  openModalPerSession?: boolean;
  onAutoOpenDismiss?: () => void;
};

export function useReservationSettingsModal({
  openModalPerSession,
  showFirstTime,
  onAutoOpenDismiss,
}: UseReservationSettingsModalProps = {}) {
  const {data, isSuccess: isSummaryLoaded} = useSummary();
  const {housing, data: reservation, hasAvantio} = useReservation();
  const {checkCanEditReservationDetails} = useReservationSettings();
  const {hasReservationGroup, isLoaded} = useGetReservationType();
  const {shouldConfirmGuests} = useComputedOnboardingDetails();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const isReady = useIsReservationSettingsReady();

  const {
    isOpen: isOpenConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
  } = useModalControls();

  const [isAutoOpened, setIsAutoOpened] = React.useState(false);

  const openModal = React.useCallback(() => {
    setIsAutoOpened(false);
    openConfirmModal();
  }, [openConfirmModal]);

  const openModalAutomatically = React.useCallback(() => {
    setIsAutoOpened(true);
    openConfirmModal();
  }, [openConfirmModal]);

  const closeModal = React.useCallback(() => {
    if (isAutoOpened) {
      onAutoOpenDismiss?.();
    }
    closeConfirmModal();
  }, [isAutoOpened, onAutoOpenDismiss, closeConfirmModal]);

  const countryCode = data?.country_code;
  const isActiveTaxes = data?.taxes?.active;
  const hasAvantioConfirmLeadGuestForm = Boolean(
    reservation &&
      reservation?.show_invoicing_fields === null &&
      isLoaded &&
      reservation?.send_lead_guest_data_on_client &&
      hasAvantio,
  );

  const availableForms = useMemo(() => {
    const isNumberOfGuestsEditable = checkCanEditReservationDetails();

    return {
      [MODAL_FORMS.numberOfGuests]: !!isNumberOfGuestsEditable,
      [MODAL_FORMS.confirmLeaderGuest]: hasAvantioConfirmLeadGuestForm,
      [MODAL_FORMS.reservationType]: hasReservationGroup,
    };
  }, [
    hasReservationGroup,
    checkCanEditReservationDetails,
    hasAvantioConfirmLeadGuestForm,
  ]);

  const isModalAvailable = Object.values(availableForms).some(Boolean);
  const leadGuestFormCompleted =
    reservation?.show_invoicing_fields !== null ||
    reservation?.show_invoicing_fields === undefined;
  // TODO i have to make this comparation, cuz from BE side i only have 3 states
  //true
  //false
  //null
  // and true and false means the form is completed, so i cant use !reservation?.show_invoicing_fields as a condition
  //this is a temporal fix, this must be fixed from BE side to return enum value
  const validateGuestAge = React.useMemo(() => {
    if (isActiveTaxes || !countryCode) {
      return {ageLimit: null};
    }

    return getAgeLimitByCountry(countryCode, housing?.police_account_type);
  }, [countryCode, isActiveTaxes, housing]);

  const isProcessComplete =
    data?.guest_registration?.status === NAVIGATION_STATUSES.complete;
  const shouldConfirmInBlock =
    Boolean(showFirstTime) &&
    isReady &&
    isLoaded &&
    Boolean(isSummaryLoaded) &&
    shouldConfirmGuests &&
    availableForms[MODAL_FORMS.numberOfGuests] &&
    !hasAvantioConfirmLeadGuestForm &&
    !isProcessComplete &&
    !leadGuestFormCompleted &&
    !isKiosko;

  useOpenGuestCountBlock(shouldConfirmInBlock);

  React.useEffect(() => {
    if (isReady) {
      if (hasAvantioConfirmLeadGuestForm) {
        openModalAutomatically();

        return;
      }
      if (showFirstTime && !shouldConfirmInBlock) {
        if (openModalPerSession && !isProcessComplete && !leadGuestFormCompleted) {
          openModalAutomatically();
          return;
        }
      }
    }
  }, [
    openModalPerSession,
    openModalAutomatically,
    isProcessComplete,
    showFirstTime,
    shouldConfirmInBlock,
    hasAvantioConfirmLeadGuestForm,
    isReady,
    leadGuestFormCompleted,
  ]);

  const Modal = React.useMemo(
    () => (
      <ReservationSettingsModal
        isOpen={isOpenConfirmModal && isModalAvailable}
        onClose={closeModal}
        availableForms={availableForms}
        ageLimit={validateGuestAge.ageLimit}
        isAutoOpened={isAutoOpened}
      />
    ),
    [
      isOpenConfirmModal,
      closeModal,
      availableForms,
      validateGuestAge.ageLimit,
      isModalAvailable,
      isAutoOpened,
    ],
  );

  return {
    isOpen: isOpenConfirmModal,
    closeModal,
    openModal,
    ageLimit: validateGuestAge.ageLimit,
    isModalAvailable,
    Modal,
    hasAvantioConfirmLeadGuestForm,
  };
}
